import { getApiBaseUrl } from "../api/baseUrl"
import { throwIfRateLimited } from "../api/rateLimit"
import type {
  ArtworkAssetRecord,
  ArtworkUploadEnvelope,
  ArtworkUploadResponse,
  CardArtwork,
} from "../schemas"
import { buildArtworkVariantUrl } from "./artwork"
import { findArtworkAssetUrl } from "./assets"
import { ArtworkError } from "./errors"

export function resolveArtworkUrl(
  artwork: CardArtwork | undefined,
  assets: readonly ArtworkAssetRecord[],
  kind: "card_art" | "thumb",
): string | null {
  const localUrl = findArtworkAssetUrl(artwork, assets, kind)
  if (localUrl) return localUrl
  if (!artwork) return null

  const variant = artwork.variants.find((entry) => entry.kind === kind)
  if (!variant) return null

  const baseUrl = getApiBaseUrl()
  if (!baseUrl) return null

  return buildArtworkVariantUrl(baseUrl, variant.assetId, kind)
}

export async function syncArtworkToServer(
  payload: ArtworkUploadEnvelope,
  sessionToken: string,
  turnstileToken?: string | null,
): Promise<ArtworkUploadResponse> {
  const baseUrl = getApiBaseUrl()
  if (!baseUrl) {
    throw new ArtworkError("ARTWORK_SYNC_NO_WORKER")
  }

  const headers: HeadersInit = {
    Authorization: `Bearer ${sessionToken}`,
    "Content-Type": "application/json",
  }
  if (turnstileToken) {
    headers["CF-Turnstile-Token"] = turnstileToken
  }

  const response = await fetch(new URL("/api/media/artwork", baseUrl), {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  })

  throwIfRateLimited(response)

  if (!response.ok) {
    const message = await response.text()
    if (response.status === 400 && message.includes("Turnstile token")) {
      throw new ArtworkError("ARTWORK_TURNSTILE_REQUIRED")
    }
    if (response.status === 403 && message.includes("Turnstile")) {
      throw new ArtworkError("ARTWORK_TURNSTILE_FAILED")
    }
    throw new ArtworkError("ARTWORK_SYNC_FAILED", message || undefined)
  }

  return (await response.json()) as ArtworkUploadResponse
}
